import * as ReactDom from 'react-dom';
import * as React from 'react';
import * as _ from 'lodash';
import { IClickableButton } from '../../types/assistant';
import { ArrowPosition } from '../../types/assistant-actions';
import { ReactDOMContainer } from '../react/reactDOMContainer/ReactDOMContainer';
import {
  TourTooltipProps,
  TourTooltipArrowProps,
} from '../react/tooltip/TourTooltipWrapper';
import { TourConfig } from '../..';

export const REACT_DOM_CONTAINER_ID = 'tour-maker-react-container';

// Yair's meeting
// todo: make an apply method, keep this stateless.
// todo: write more e2e's, before refactoring.
// todo: 'Keep or another' tempalet, keep the template in the tourMaker, but take out the hooks of ADI.
//       add ability to merge templates from tourmaker.config (for adding the ADI hooks once)

export interface TooltipState {
  setComponent(component: React.ComponentType<TourTooltipProps>): TooltipState;
  setArrowComponent(
    arrowComponent: React.ComponentType<TourTooltipArrowProps>,
  ): TooltipState;
  setAttachedTo(
    attachToElement: HTMLElement,
    position: ArrowPosition,
  ): TooltipState;
  setButtons(buttons: IClickableButton[]): TooltipState;
  setSuccessMessage(): TooltipState;
  setShowCloseButton(showCloseButton: boolean): TooltipState;
  setOnClickButtonFn(onClickButton: () => TooltipState): TooltipState;
  setContent(content: string): TooltipState;
  setTitle(title: string): TooltipState;
  setCustomProps(customProps: { [key: string]: any }): TooltipState;
  setActiveAction(activeAction: number): TooltipState;
  setCustomStyle(style: { [key: string]: string | number }): TooltipState;
  setStopTourFn(stopTourFn: () => void): TooltipState;
  show(): TooltipState;
  hide(): TooltipState;
  setArrowPosition(arrowPosition: ArrowPosition): TooltipState;
  apply(): TooltipState;
  doNextIf(condition: Boolean): TooltipState;
}

const TOOLTIP_DOMAIN = 'tooltip';

export class DOMUtils {
  private reactContainerRef;
  private nextDOMState = {};
  private ignoreNextStateChange = false;

  constructor() {}

  apply() {
    // todo: a builder that returns the DOMutils.toolbar or DOMutils.backdrop or DOMutils.hint and apply method.
    // will be consumed as:
    // tooltipController.setButtons(bla).setContent(blbl).apply().
    const nextState = _.merge(this.reactContainerRef.state, this.nextDOMState);
    this.reactContainerRef.setState(nextState);
    this.nextDOMState = {};
  }

  setState(domain: string, state) {
    if (this.ignoreNextStateChange) {
      this.ignoreNextStateChange = false;
      return;
    }

    this.nextDOMState[domain] = _.merge(this.nextDOMState[domain] || {}, state);
  }

  addReactContainerToDOM(rootElement: HTMLElement = null) {
    const reactContainerElement = document.createElement('div');
    reactContainerElement.id = REACT_DOM_CONTAINER_ID;
    rootElement.appendChild(reactContainerElement);

    this.reactContainerRef = ReactDom.render(
      React.createElement(ReactDOMContainer),
      reactContainerElement,
    );
  }

  resetTooltip() {
    this.reactContainerRef.setState({ tooltip: { show: false } });
  }

  setTooltipStateBuilder(): TooltipState {
    const stateChanger: TooltipState = {
      doNextIf(condition: Boolean): TooltipState {
        this.ignoreNextStateChange = !condition;
        return stateChanger;
      },
      setComponent: (component: React.ComponentType<TourTooltipProps>) => {
        this.setState(TOOLTIP_DOMAIN, { component });
        return stateChanger;
      },

      setArrowComponent: (
        arrowComponent: React.ComponentType<TourTooltipArrowProps>,
      ) => {
        this.setState(TOOLTIP_DOMAIN, { arrowComponent });
        return stateChanger;
      },

      setAttachedTo: (
        attachToElement: HTMLElement,
        position: ArrowPosition,
      ) => {
        const rect = attachToElement.getBoundingClientRect();
        const arrowLeftPosition = rect.left + rect.width / 2;

        this.setState(TOOLTIP_DOMAIN, {
          arrowLeftPosition,
          position,
        });

        return stateChanger;
      },

      setButtons: (buttons: IClickableButton[]) => {
        this.setState(TOOLTIP_DOMAIN, { buttons });
        return stateChanger;
      },

      setSuccessMessage: () => {
        this.setState(TOOLTIP_DOMAIN, {
          successMessage: true,
        });
        return stateChanger;
      },

      setShowCloseButton: (showCloseButton: boolean) => {
        this.setState(TOOLTIP_DOMAIN, {
          showCloseButton,
        });
        return stateChanger;
      },

      setOnClickButtonFn: (onClickButton: () => void) => {
        this.setState(TOOLTIP_DOMAIN, {
          onClickButton,
        });
        return stateChanger;
      },

      setContent: (content: string) => {
        this.setState(TOOLTIP_DOMAIN, {
          content,
        });
        return stateChanger;
      },

      setCustomProps: (customProps) => {
        this.setState(TOOLTIP_DOMAIN, {
          customProps,
        });
        return stateChanger;
      },

      setTitle: (title: string) => {
        this.setState(TOOLTIP_DOMAIN, {
          title,
        });
        return stateChanger;
      },

      setActiveAction: (activeActionIndex: number) => {
        this.setState(TOOLTIP_DOMAIN, {
          activeActionIndex,
        });
        return stateChanger;
      },

      setCustomStyle: (style: { [key: string]: string | number }) => {
        this.setState(TOOLTIP_DOMAIN, {
          style,
        });

        return stateChanger;
      },

      setStopTourFn: (stopTourFn: () => void) => {
        this.setState(TOOLTIP_DOMAIN, {
          stopTourFn,
        });

        return stateChanger;
      },

      show: () => {
        this.setState(TOOLTIP_DOMAIN, {
          show: true,
        });

        return stateChanger;
      },

      hide: () => {
        this.setState(TOOLTIP_DOMAIN, {
          show: false,
        });

        return stateChanger;
      },

      setArrowPosition: (arrowPosition: ArrowPosition) => {
        this.setState(TOOLTIP_DOMAIN, {
          arrowPosition,
        });

        return stateChanger;
      },

      apply: this.apply.bind(this),
    };

    return stateChanger;
  }

  showBackdrop(
    opacity: number,
    customStyle: TourConfig['customBackdropStyle'],
  ) {
    this.reactContainerRef.setState({
      backdrop: {
        ...this.reactContainerRef.state.backdrop,
        show: true,
        opacity,
        customStyle,
      },
    });
  }

  hideBackdrop() {
    this.reactContainerRef.setState({
      backdrop: { ...this.reactContainerRef.state.backdrop, show: false },
    });
  }

  showLoader(component?: TourConfig['loaderComponent']) {
    this.reactContainerRef.setState({
      loader: {
        ...this.reactContainerRef.state.loader,
        show: true,
        component,
      },
    });
  }

  hideLoader() {
    this.reactContainerRef.setState({
      loader: { ...this.reactContainerRef.state.loader, show: false },
    });
  }

  showHint() {
    this.reactContainerRef.setState({
      hint: { ...this.reactContainerRef.state.hint, show: true },
    });
  }

  hideHint() {
    this.reactContainerRef.setState({
      hint: { ...this.reactContainerRef.state.hint, show: false },
    });
  }

  clickHint(clicked: boolean) {
    this.reactContainerRef.setState({
      hint: { ...this.reactContainerRef.state.hint, simulateClick: clicked },
    });
  }
}
